import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { ArrowLeft, Mail, MessageCircle, Loader2, ExternalLink } from 'lucide-react';
import FloatingWhatsApp from '@/components/FloatingWhatsApp';

function normalizePhone(raw) {
  const digits = String(raw || '').replace(/\D/g, '');
  if (!digits) return '';
  if (digits.startsWith('62')) return digits;
  if (digits.startsWith('0')) return '62' + digits.slice(1);
  if (digits.startsWith('8')) return '62' + digits;
  return digits;
}

function formatPhoneDisplay(raw) {
  const d = normalizePhone(raw);
  if (!d) return '';
  // 62XXX XXXX XXXX → +62 XXX-XXXX-XXXX
  const rest = d.slice(2);
  return `+62 ${rest.slice(0, 3)}-${rest.slice(3, 7)}-${rest.slice(7)}`.replace(/-+$/, '').replace(/-(?=$)/, '');
}

export default function Help() {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/widget-settings')
      .then((res) => setCfg(res.data))
      .catch(() => setCfg({}))
      .finally(() => setLoading(false));
  }, []);

  const phone = normalizePhone(cfg?.support_whatsapp);
  const email = cfg?.support_email || 'support@tokomoe.com';
  const waMessage = cfg?.whatsapp_widget_message || 'Halo, saya butuh bantuan dengan Tokomoe.';
  const waHref = phone ? `https://wa.me/${phone}?text=${encodeURIComponent(waMessage)}` : null;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="help-page">
      <header className="bg-white border-b border-slate-200">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors">
            <ArrowLeft size={18} /> <span className="text-sm font-medium">Kembali ke Beranda</span>
          </Link>
          <img src="/logo.png" alt="Tokomoe" className="h-8 object-contain" />
        </div>
      </header>

      <main className="container mx-auto px-4 md:px-6 lg:px-8 py-12 max-w-3xl">
        <div className="text-center mb-10">
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900 mb-2" style={{ fontFamily: 'Plus Jakarta Sans' }}>
            Pusat Bantuan
          </h1>
          <p className="text-slate-600 max-w-xl mx-auto">
            Tim support Tokomoe siap membantu. Pilih kanal yang paling nyaman buat kamu — kami balas secepat mungkin di jam kerja.
          </p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20 text-slate-500">
            <Loader2 size={20} className="animate-spin mr-2" /> Memuat kontak support...
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-5 mb-10">
            {/* WhatsApp Card */}
            <Card className="border-slate-200 shadow-sm hover:shadow-md transition-shadow">
              <CardContent className="p-6 flex flex-col h-full">
                <div className="w-12 h-12 rounded-xl bg-emerald-50 flex items-center justify-center mb-4">
                  <MessageCircle size={24} className="text-emerald-600" />
                </div>
                <h2 className="text-lg font-semibold text-slate-900 mb-1">Chat WhatsApp</h2>
                <p className="text-sm text-slate-600 mb-4 flex-1">
                  Respon paling cepat untuk pertanyaan teknis, kendala pembayaran, atau bantuan setup toko.
                </p>
                {phone ? (
                  <>
                    <p className="text-sm font-mono text-slate-700 mb-3">{formatPhoneDisplay(phone)}</p>
                    <a
                      href={waHref}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="help-wa-button"
                      className="inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1FB955] text-white rounded-lg px-4 py-2.5 text-sm font-semibold transition-colors"
                    >
                      <MessageCircle size={16} /> Hubungi via WhatsApp
                      <ExternalLink size={14} className="opacity-70" />
                    </a>
                  </>
                ) : (
                  <p className="text-sm text-slate-500 italic">Nomor WhatsApp belum dikonfigurasi.</p>
                )}
              </CardContent>
            </Card>

            {/* Email Card */}
            <Card className="border-slate-200 shadow-sm hover:shadow-md transition-shadow">
              <CardContent className="p-6 flex flex-col h-full">
                <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center mb-4">
                  <Mail size={24} className="text-[#2D6CDF]" />
                </div>
                <h2 className="text-lg font-semibold text-slate-900 mb-1">Email Support</h2>
                <p className="text-sm text-slate-600 mb-4 flex-1">
                  Untuk dokumentasi, penagihan, integrasi, atau pertanyaan yang butuh lampiran file.
                </p>
                <p className="text-sm font-mono text-slate-700 mb-3 break-all">{email}</p>
                <a
                  href={`mailto:${email}`}
                  data-testid="help-email-button"
                  className="inline-flex items-center justify-center gap-2 bg-[#2D6CDF] hover:bg-[#1A5BC4] text-white rounded-lg px-4 py-2.5 text-sm font-semibold transition-colors"
                >
                  <Mail size={16} /> Kirim Email
                </a>
              </CardContent>
            </Card>
          </div>
        )}

        <Card className="border-slate-200">
          <CardContent className="p-6">
            <h3 className="text-base font-semibold text-slate-900 mb-3">Sumber Bantuan Lain</h3>
            <ul className="space-y-2.5 text-sm">
              <li>
                <Link to="/#faq" className="text-[#2D6CDF] hover:underline inline-flex items-center gap-1.5">
                  Lihat FAQ — pertanyaan yang sering ditanyakan <ExternalLink size={13} />
                </Link>
              </li>
              <li>
                <Link to="/api-docs" className="text-[#2D6CDF] hover:underline inline-flex items-center gap-1.5">
                  Dokumentasi API <ExternalLink size={13} />
                </Link>
              </li>
              <li>
                <Link to="/status" className="text-[#2D6CDF] hover:underline inline-flex items-center gap-1.5">
                  Status Sistem (real-time) <ExternalLink size={13} />
                </Link>
              </li>
            </ul>
          </CardContent>
        </Card>

        <p className="text-xs text-slate-500 text-center mt-8">
          Jam operasional support: Senin–Jumat 09.00–17.00 WIB. Di luar jam tersebut respon mungkin lebih lama.
        </p>
      </main>

      <FloatingWhatsApp />
    </div>
  );
}
